import { useAppDispatch, useAppSelector } from 'hooks/redux';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { createGlobalState } from 'react-use';

import { List as ImmutableList } from 'immutable';

import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/system/Box';

import { makeStyles } from 'tss-react/mui';

import { tracker } from 'app-components/utils/core';
import { isDevBuild } from 'app-components/utils/core/config';
import { makeAiPromptRequestAction, makeClearAiConversationAction } from 'data/aiConversations/aiConversationsActions';
import { getAiConversations } from 'data/aiConversations/aiConversationsSelectors';
import { AiRoleEnum, mkAiPromptRequest, mkChatTurn } from 'data/aiConversations/aiConversationsTypes';
import useSpeechRecognition from 'utils/speech/speechRecognition';
import useSpeechSynthesis from 'utils/speech/useSpeechSynthesis';

import { stdSliceSelectors } from './data/aiMemories/aiMemoriesSelectors';
import { aiMemoriesActions } from './data/aiMemories/aiMemoriesSlice';

import ChatDisplay from './ChatDisplay';
import ChatHeader from './ChatHeader';
import ChatPromptField from './ChatPromptField';
import SamplePromptsPage from './SamplePromptsPage';
import SystemPromptDialog from './SystemPromptDialog';

import { v4 as uuidv4 } from 'uuid';
import { TONE_OPTIONS, aiExpertsConfig } from '../aiConfig';
import { trackingEvents } from './aiEvents';

export const useAutoConversationState = createGlobalState(false);

function mergeFeedback(currentConversation, messageList) {
  return currentConversation.map((message, index) => {
    const previousMessage = messageList.get(index);
    if (previousMessage) {
      return {
        ...message,
        ...Object.fromEntries(Object.entries(previousMessage).filter(([key]) => key !== 'role' && key !== 'message')),
      };
    }
    return message;
  });
}

function sendMixpanelEvent(event, payload) {
  if (event) {
    tracker.track(event, payload);
  }
}

const MainChatPage = (props) => {
  const { visible, onClose, context, gradients, expertName, allowVoices, headerOptions = [] } = props;
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const theme = useTheme();
  const [limitReached, setLimitReached] = useState(false);
  const [autoConversation, setAutoConversation] = useAutoConversationState(true);
  const { startRecognition } = useSpeechRecognition();
  const [userPrompt, setUserPrompt] = useState('');
  const [processing, setProcessing] = useState(false);
  const [tone, setTone] = useState(TONE_OPTIONS[0].id);
  const [expandWindow, setExpandWindow] = useState(false);
  const { classes } = useStyles(expandWindow);
  const currentConversation = useSelector(getAiConversations, shallowEqual);
  const [messageList, setMessageList] = useState(currentConversation?.size ? currentConversation : ImmutableList([]));

  const userMemories = useAppSelector(stdSliceSelectors.selectAll);

  const {
    agentId,
    disclaimer2,
    overrideSystemPrompt,
    overrideReminderPrompt,
    promptPlaceholder,
    samplePrompts,
    samplePromptsHaveIcons,
    responseIsJson,
  } = aiExpertsConfig[expertName];

  const [systemPromptDialogOpen, setSystemPromptDialogOpen] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState(overrideSystemPrompt || '');

  const mergeFeedbackMemoized = useCallback(() => {
    if (currentConversation?.size) {
      setMessageList((prevMessageList) => mergeFeedback(currentConversation, prevMessageList));
    }
  }, [currentConversation]);

  useEffect(() => {
    setSystemPrompt(overrideSystemPrompt || '');
  }, [overrideSystemPrompt]);

  useEffect(() => {
    if (appDispatch) {
      appDispatch(aiMemoriesActions.fetch());
    }
  }, [appDispatch]);

  useEffect(() => {
    if (visible) {
      sendMixpanelEvent(trackingEvents?.aiFlowStarted);
    } else {
      sendMixpanelEvent(trackingEvents?.aiFlowEnd);
    }
  }, [visible]);

  useEffect(() => {
    setProcessing(false);
    setUserPrompt('');
    mergeFeedbackMemoized();
  }, [currentConversation, mergeFeedbackMemoized]);

  const processFeedbackSubmit = (feedback) => {
    const obj = messageList.get(feedback.messageIndex);
    if (obj) {
      obj.feedback = feedback;
      const newMessageList = messageList.set(feedback.messageIndex, obj);
      setMessageList(newMessageList);
      if (trackingEvents?.aiFeedback) {
        sendMixpanelEvent(trackingEvents.aiFeedback, feedback);
      }
    }
  };

  const processSubmit = (prompt, source = 'user') => {
    setUserPrompt('');
    const placeholderPrompt = responseIsJson ? '{ "response": "Thinking..." }' : 'Thinking...';
    setMessageList(
      messageList.push(mkChatTurn(AiRoleEnum.USER, prompt)).push(mkChatTurn(AiRoleEnum.ASSISTANT, placeholderPrompt)),
    );
    let trackEvent;
    switch (source) {
      case 'sample':
        trackEvent = trackingEvents?.aiFlowSelectQuestion;
        break;
      case 'followup':
        trackEvent = trackingEvents?.aiFlowFollowupQuestion;
        break;
      default:
        trackEvent = trackingEvents?.aiFlowEnterQuestion;
    }
    if (trackEvent) {
      sendMixpanelEvent(trackEvent, {
        path: window.location.href,
        providedQuestion: prompt,
        questionCount: messageList?.filter((item) => item.role === 'USER').size + 1 || 0,
      });
    }
    dispatchQuestion(prompt);
  };

  const onSpeechEnd = useCallback(() => {
    if (autoConversation) {
      startRecognition?.();
    }
  }, [autoConversation, startRecognition]);

  const speak = useSpeechSynthesis(onSpeechEnd);

  const handleToneChange = (event) => {
    setTone(event.target.value);
  };

  const handleConvoChange = () => {
    setMessageList(ImmutableList([]));
    if (trackingEvents?.aiFlowNewChat) {
      sendMixpanelEvent(trackingEvents.aiFlowNewChat, {
        path: window.location.href,
      });
    }
    dispatch(makeClearAiConversationAction());
  };

  const dispatchQuestion = useCallback(
    (value) => {
      if (processing || !value) return;
      setProcessing(true);
      setUserPrompt(null);

      const addlPrompt = `Please remember to return your response in the prescribed JSON format.  Also, for your reference, here are the Prior Memories I have for the user: ${userMemories
        .map((m) => m.data.memory)
        .join(', ')}`;
      const newPostData = mkAiPromptRequest({
        agent: agentId,
        prompt: value,
        context,
        history: messageList.toJS(),
        tone,
        additionalSystemPrompt: addlPrompt,
        overrideReminderPrompt,
        overrideSystemPrompt: systemPrompt,
      });

      new Promise((resolve, reject) => dispatch(makeAiPromptRequestAction(newPostData, { resolve, reject })))
        .then((response) => {
          setLimitReached(false);
          if (autoConversation && response.data?.assistantResponse) {
            speak(response.data.assistantResponse);
          }
          if (responseIsJson) {
            try {
              const responseObject = JSON.parse(response.data.assistantResponse);
              if (responseObject) {
                if (responseObject.memory?.length > 0) {
                  responseObject.memory.forEach((memory) => {
                    dispatch(
                      aiMemoriesActions.create({
                        clientId: uuidv4().toUpperCase(),
                        data: {
                          name: '',
                          memory: memory.trim(),
                          priority: 0,
                        },
                      }),
                    );
                  });
                }
              }
            } catch (e) {
              // do nothing
            }
          }
        })
        .catch((error) => {
          if (error.status === 429) {
            setLimitReached(true);
          }
          setProcessing(false);
        });
    },
    [
      responseIsJson,
      userMemories,
      autoConversation,
      context,
      dispatch,
      processing,
      speak,
      tone,
      agentId,
      systemPrompt,
      overrideReminderPrompt,
      messageList,
    ],
  );

  const toneMenuItems = TONE_OPTIONS.map((toneOption) => ({
    label: toneOption.label,
    value: toneOption.id,
  }));

  const allHeaderOptions = allowVoices
    ? [
        {
          value: tone,
          id: 'assistant-tone',
          label: 'Assistant Tone:',
          onChange: handleToneChange,
          menuItems: toneMenuItems,
        },
      ]
    : [].concat(headerOptions);

  return (
    <>
      <Slide direction='left' in={visible}>
        <Paper className={classes.modalWrapper} elevation={24}>
          <Paper className={classes.chatContainer} id='chat-paper'>
            <ChatHeader
              options={allHeaderOptions}
              onConvoChange={handleConvoChange}
              onClose={onClose}
              expandWindow={expandWindow}
              onExpandToggle={(value) => {
                if (trackingEvents.aiExpanded && !expandWindow) {
                  sendMixpanelEvent(trackingEvents.aiExpanded, {
                    path: window.location.href,
                    questionCount: messageList?.filter((item) => item.role === 'USER').size + 1 || 0,
                  });
                }
                setExpandWindow(value);
              }}
            />

            <Box
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
                zIndex: 1,
                position: 'relative',
                paddingTop: '80px',
              }}
            >
              {messageList.size === 0 ? (
                <SamplePromptsPage
                  gradients={gradients}
                  samplePrompts={samplePrompts}
                  samplePromptsHaveIcons={samplePromptsHaveIcons}
                  submitAutoPrompt={(prompt) => processSubmit(prompt, 'sample')}
                />
              ) : (
                <ChatDisplay
                  messages={messageList}
                  setAutoConversation={setAutoConversation}
                  submitAutoPrompt={(prompt) => processSubmit(prompt, 'followup')}
                  isProcessing={processing}
                  onFeedbackSubmit={processFeedbackSubmit}
                  responseIsJson={responseIsJson}
                />
              )}
            </Box>

            <Box
              sx={{
                width: '100%',
                backgroundColor: alpha(theme.palette.background.paper, 0.74),
                backdropFilter: `blur(8px)`,
                bottom: 0,
                zIndex: 2,
              }}
            >
              <Stack gap={0.5}>
                <Divider />
                <Box sx={{ padding: theme.spacing(2, 3, 5, 3) }}>
                  <ChatPromptField
                    initialValue={userPrompt}
                    disabled={processing}
                    placeholder={processing ? 'Thinking...' : promptPlaceholder}
                    onSubmit={processSubmit}
                    label={disclaimer2}
                    autoConverstion={autoConversation}
                    setAutoConversation={setAutoConversation}
                  />
                  {isDevBuild() && (
                    <Link
                      component='button'
                      variant='body2'
                      onClick={() => setSystemPromptDialogOpen(true)}
                      sx={{ mt: 1, display: 'inline-block' }}
                    >
                      Edit system prompt
                    </Link>
                  )}
                  {limitReached && <Alert severity='warning'>Request limit reached. Please try again later.</Alert>}
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Paper>
      </Slide>
      <Slide direction='left' in={visible}>
        <Box className={classes.blurLayer}></Box>
      </Slide>
      <SystemPromptDialog
        open={systemPromptDialogOpen}
        onClose={() => setSystemPromptDialogOpen(false)}
        onSave={(newPrompt) => setSystemPrompt(newPrompt?.length > 10 ? newPrompt : overrideSystemPrompt)}
        initialPrompt={systemPrompt}
      />
    </>
  );
};

const useStyles = makeStyles()((theme, expandWindow) => ({
  modalWrapper: {
    position: 'fixed',
    top: theme.spacing(10),
    right: theme.spacing(3),
    transition: 'width .5s ease-in-out',
    width: expandWindow ? '80vw' : '37vw',
    minHeight: '500px',
    height: '90vh',
    minWidth: '600px',
    boxShadow: 24,
    overflow: 'hidden',
    zIndex: theme.zIndex.modal,
    [theme.breakpoints.down('md')]: {
      minWidth: '375px',
      width: '100vw',
      height: '100vh',
      top: 0,
      right: 0,
    },
    backgroundColor: theme.palette.background.paper,
  },
  blurLayer: {
    position: 'fixed',
    bottom: 0,
    right: 0,
    transition: 'width .5s ease-in-out',
    width: '37vw',
    minHeight: '400px',
    height: '40vh',
    minWidth: '300px',
    filter: 'blur(24px)',
    backdropFilter: 'blur(8px)',
    zIndex: 99,
    [theme.breakpoints.down('md')]: {
      minWidth: '375px',
      width: '100vw',
      height: '100vh',
      top: 0,
      right: 0,
    },
    backgroundColor: alpha(theme.palette.background.paper, 0.4),
  },
  chatContainer: {
    background: `linear-gradient(57deg, ${alpha(theme.palette.data.sequenceLight[0], 0.1)} 20%, ${alpha(
      theme.palette.data.sequenceLight[9],
      0.04,
    )} 100%)`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
  },
}));

MainChatPage.propTypes = {
  context: PropTypes.object,
  gradients: PropTypes.array,
  expertName: PropTypes.string,
  headerOptions: PropTypes.array,
  allowVoices: PropTypes.bool,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  userMemoryId: PropTypes.string,
};

export default MainChatPage;
